/**
 * MasterMeasuresCard — Verwaltung der „Übergeordneten Maßnahmen" (Träger-
 * Vergaben mit Vergabenummer/Maßnahmenummer) und der zugehörigen
 * Auftraggeber-Stammdaten. Sichtbar für Admin / GF / NL / SL
 * (Berechtigung: massnahmen:edit).
 */
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { DatePickerDE, isoToDe } from '../../components/ui/date-picker-de';
import { Layers, Plus, Pencil, Trash2, Briefcase, Loader2, Download, Copy, CalendarPlus, Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, Link2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { AssignMassnahmenDialog } from './AssignMassnahmenDialog';

const ART_LABEL = {
  ba: 'Bundesagentur für Arbeit', jobcenter: 'Jobcenter',
  landkreis: 'Landkreis', kommune: 'Kommune', bamf: 'BAMF', sonstiges: 'Sonstiges',
};

const EMPTY_AG = { name: '', art: 'jobcenter', ansprechpartner: '', email: '', telefon: '', strasse: '', plz: '', ort: '', notes: '' };
const EMPTY_MM = { name: '', vergabenummer: '', los_nr: '', lfd_nr: '', massnahmenummer: '', bamf_kursnummer: '', location_ids: [], auftraggeber_id: '', foerder_start: '', foerder_end: '', verlaengerungs_option: false, verlaengerung_max_end: '', teilnehmer_plaetze: '', budget: '', is_active: true, notes: '' };

export const MasterMeasuresCard = () => {
  const [auftraggeber, setAuftraggeber] = useState([]);
  const [measures, setMeasures] = useState([]);
  const [locations, setLocations] = useState([]);
  const [locFilter, setLocFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [agOpen, setAgOpen] = useState(false);
  const [agEdit, setAgEdit] = useState(null);
  const [mmOpen, setMmOpen] = useState(false);
  const [mmEdit, setMmEdit] = useState(null);
  const [mmIsCopy, setMmIsCopy] = useState(false);
  const [copySource, setCopySource] = useState(null);
  const [assignedMassnahmen, setAssignedMassnahmen] = useState([]);
  const [selectedAssignIds, setSelectedAssignIds] = useState([]);
  const [loadingAssigned, setLoadingAssigned] = useState(false);
  const [agForm, setAgForm] = useState(EMPTY_AG);
  const [mmForm, setMmForm] = useState(EMPTY_MM);
  const [saving, setSaving] = useState(false);
  const [expMeasure, setExpMeasure] = useState(null);
  const [assignMeasure, setAssignMeasure] = useState(null);
  const [expMonth, setExpMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [exporting, setExporting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  // XLSX-Import
  const [importOpen, setImportOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [exportingAll, setExportingAll] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const importFileRef = React.useRef(null);

  const downloadTemplate = async () => {
    setDownloadingTemplate(true);
    try {
      const res = await api.get('/master-measures/import/template', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Vorlage_Uebergeordnete_Massnahmen.xlsx';
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Vorlage konnte nicht geladen werden');
    } finally { setDownloadingTemplate(false); }
  };

  const exportAll = async () => {
    setExportingAll(true);
    try {
      const params = locFilter && locFilter !== 'all' ? { location_id: locFilter } : {};
      const res = await api.get('/master-measures/export', { params, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      const ts = new Date().toISOString().slice(0, 10);
      a.download = `Uebergeordnete_Massnahmen_${ts}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Export fehlgeschlagen');
    } finally { setExportingAll(false); }
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (importFileRef.current) importFileRef.current.value = '';
    if (!file) return;
    setImportFile(file);
    setImportResult(null);
    setPreviewing(true);
    setImportPreview(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/master-measures/import/preview', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImportPreview(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Vorschau fehlgeschlagen');
      setImportFile(null);
    } finally { setPreviewing(false); }
  };

  const confirmImport = async () => {
    if (!importFile) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', importFile);
      const res = await api.post('/master-measures/import', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImportResult(res.data);
      setImportPreview(null);
      setImportFile(null);
      const total = (res.data.created || 0) + (res.data.updated || 0);
      if (total > 0) {
        toast.success(`${res.data.created} angelegt, ${res.data.updated} aktualisiert`);
        load();
      } else {
        toast.info('Keine Vergaben importiert');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Import fehlgeschlagen');
    } finally { setImporting(false); }
  };

  const resetImport = () => {
    setImportFile(null); setImportPreview(null); setImportResult(null);
  };

  useEffect(() => {
    if (!expMeasure || !expMonth) { setPreview(null); return; }
    const [year, month] = expMonth.split('-').map(Number);
    let cancelled = false;
    setPreviewLoading(true);
    api.get(`/master-measures/${expMeasure.id}/export/preview`, { params: { year, month } })
      .then((res) => { if (!cancelled) setPreview(res.data); })
      .catch(() => { if (!cancelled) setPreview(null); })
      .finally(() => { if (!cancelled) setPreviewLoading(false); });
    return () => { cancelled = true; };
  }, [expMeasure, expMonth]);

  const load = async () => {
    try {
      const params = locFilter && locFilter !== 'all' ? { location_id: locFilter } : {};
      const [ag, mm, locs] = await Promise.all([
        api.get('/auftraggeber'),
        api.get('/master-measures', { params }),
        api.get('/locations', { params: { is_active: true } }),
      ]);
      setAuftraggeber(ag.data || []);
      setMeasures(mm.data || []);
      setLocations(locs.data || []);
    } catch {
      // admin only — ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locFilter]);

  // --- Auftraggeber ---
  const openAg = (a = null) => {
    setAgEdit(a); setAgForm(a ? { ...EMPTY_AG, ...a } : EMPTY_AG); setAgOpen(true);
  };
  const saveAg = async () => {
    if (!agForm.name?.trim()) return toast.error('Name ist Pflicht');
    setSaving(true);
    try {
      if (agEdit) await api.put(`/auftraggeber/${agEdit.id}`, agForm);
      else await api.post('/auftraggeber', agForm);
      toast.success('Auftraggeber gespeichert');
      setAgOpen(false); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Fehler');
    } finally { setSaving(false); }
  };
  const deleteAg = async (a) => {
    if (!window.confirm(`Auftraggeber „${a.name}" löschen?`)) return;
    try { await api.delete(`/auftraggeber/${a.id}`); toast.success('Gelöscht'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Fehler'); }
  };

  // --- Master Measures ---
  const openMm = (m = null) => {
    setMmIsCopy(false);
    setCopySource(null);
    setAssignedMassnahmen([]);
    setSelectedAssignIds([]);
    setMmEdit(m); setMmForm(m ? { ...EMPTY_MM, ...m, budget: m.budget ?? '', teilnehmer_plaetze: m.teilnehmer_plaetze ?? '' } : EMPTY_MM); setMmOpen(true);
  };
  // Kopiert eine bestehende Vergabe als Vorlage für eine neue (z. B. neue Förderperiode).
  // Übernimmt alle Stammdaten; legt beim Speichern eine NEUE Vergabe an (kein PUT).
  // Zugeordnete operative Maßnahmen können optional ausgewählt mitübernommen werden.
  const copyMm = async (m) => {
    const picked = {};
    Object.keys(EMPTY_MM).forEach((k) => {
      picked[k] = m[k] !== undefined && m[k] !== null ? m[k] : EMPTY_MM[k];
    });
    setMmEdit(null);
    setMmIsCopy(true);
    setCopySource(m);
    setMmForm({
      ...picked,
      name: `${m.name} (Kopie)`,
      budget: m.budget ?? '',
      teilnehmer_plaetze: m.teilnehmer_plaetze ?? '',
    });
    setMmOpen(true);
    // Zugeordnete operative Maßnahmen laden (zur Auswahl)
    setAssignedMassnahmen([]);
    setSelectedAssignIds([]);
    if (m.assigned_count > 0) {
      setLoadingAssigned(true);
      try {
        const res = await api.get(`/master-measures/${m.id}/assigned-massnahmen`);
        const list = res.data || [];
        setAssignedMassnahmen(list);
        setSelectedAssignIds(list.map((x) => x.id)); // standardmäßig alle ausgewählt
      } catch {
        setAssignedMassnahmen([]);
      } finally {
        setLoadingAssigned(false);
      }
    }
  };
  const toggleMmLoc = (id) => {
    setMmForm((f) => {
      const cur = f.location_ids || [];
      return { ...f, location_ids: cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id] };
    });
  };
  const toggleAssign = (id) => {
    setSelectedAssignIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };
  // Verschiebt ein ISO-Datum (YYYY-MM-DD) um n Jahre (29.02. → 28.02. in Nicht-Schaltjahren).
  const addYearsIso = (iso, n) => {
    if (!iso) return iso;
    const d = new Date(`${iso}T00:00:00`);
    if (isNaN(d.getTime())) return iso;
    const month = d.getMonth();
    d.setFullYear(d.getFullYear() + n);
    if (d.getMonth() !== month) d.setDate(0);
    return d.toISOString().slice(0, 10);
  };
  const shiftFoerderYear = () => {
    setMmForm((f) => ({
      ...f,
      foerder_start: addYearsIso(f.foerder_start, 1),
      foerder_end: addYearsIso(f.foerder_end, 1),
      verlaengerung_max_end: addYearsIso(f.verlaengerung_max_end, 1),
    }));
    toast.success('Förderzeitraum um 1 Jahr verschoben');
  };
  const saveMm = async () => {
    if (!mmForm.name?.trim()) return toast.error('Name ist Pflicht');
    const payload = {
      ...mmForm,
      auftraggeber_id: mmForm.auftraggeber_id || null,
      foerder_start: mmForm.foerder_start || null,
      foerder_end: mmForm.foerder_end || null,
      verlaengerung_max_end: mmForm.verlaengerungs_option ? (mmForm.verlaengerung_max_end || null) : null,
      teilnehmer_plaetze: mmForm.teilnehmer_plaetze === '' ? null : parseInt(mmForm.teilnehmer_plaetze) || null,
      budget: mmForm.budget === '' ? null : parseFloat(mmForm.budget) || null,
    };
    setSaving(true);
    try {
      if (mmEdit) {
        await api.put(`/master-measures/${mmEdit.id}`, payload);
      } else if (mmIsCopy && copySource) {
        await api.post(`/master-measures/${copySource.id}/duplicate`, {
          ...payload,
          assign_massnahme_ids: selectedAssignIds,
        });
      } else {
        await api.post('/master-measures', payload);
      }
      const assignedNote = mmIsCopy && selectedAssignIds.length > 0
        ? ` (${selectedAssignIds.length} Zuordnung${selectedAssignIds.length === 1 ? '' : 'en'} übernommen)`
        : '';
      toast.success(mmIsCopy ? `Kopie als neue Vergabe gespeichert${assignedNote}` : 'Übergeordnete Maßnahme gespeichert');
      setMmOpen(false); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Fehler');
    } finally { setSaving(false); }
  };
  const runExport = async () => {
    if (!expMeasure || !expMonth) return;
    const [year, month] = expMonth.split('-').map(Number);
    setExporting(true);
    try {
      const res = await api.get(`/master-measures/${expMeasure.id}/export`, {
        params: { year, month },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(expMeasure.name || 'Massnahme').replace(/[^\wäöüÄÖÜß-]+/g, '_')}_${expMonth}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export erstellt');
      setExpMeasure(null);
    } catch {
      toast.error('Export fehlgeschlagen');
    } finally { setExporting(false); }
  };

  const deleteMm = async (m) => {
    if (!window.confirm(`Übergeordnete Maßnahme „${m.name}" löschen?`)) return;
    try { await api.delete(`/master-measures/${m.id}`); toast.success('Gelöscht'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Fehler'); }
  };

  if (loading) return null;

  return (
    <Card className="border-border/60" data-testid="master-measures-card">
      <CardHeader>
        <CardTitle className="font-heading flex items-center gap-2">
          <Layers className="h-5 w-5 text-primary" />
          Übergeordnete Maßnahmen
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Auftraggeber-Stammdaten */}
        <section>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-2">
            <div>
              <h3 className="text-sm font-medium flex items-center gap-1.5">
                <Briefcase className="h-4 w-4 text-muted-foreground" /> Auftraggeber-Stammdaten
              </h3>
              <p className="text-xs text-muted-foreground">Jobcenter, BA-Standorte, Landkreise — werden in Vergaben referenziert.</p>
            </div>
            <Button size="sm" variant="outline" onClick={() => openAg()} className="rounded-full w-full sm:w-auto sm:shrink-0" data-testid="add-auftraggeber-btn">
              <Plus className="h-4 w-4 mr-1" /> Neu
            </Button>
          </div>
          {auftraggeber.length === 0 ? (
            <p className="text-sm text-muted-foreground py-3 text-center bg-secondary/20 rounded-md">Noch keine Auftraggeber angelegt.</p>
          ) : (
            <div className="rounded-md border border-border divide-y divide-border">
              {auftraggeber.map((a) => (
                <div key={a.id} className="flex items-center gap-3 px-3 py-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium truncate">{a.name}</span>
                      <Badge variant="outline" className="text-xs">{ART_LABEL[a.art] || a.art}</Badge>
                    </div>
                    {(a.ort || a.email || a.ansprechpartner) && (
                      <div className="text-xs text-muted-foreground truncate">
                        {[a.ansprechpartner, a.email, a.ort].filter(Boolean).join(' · ')}
                      </div>
                    )}
                  </div>
                  <Button size="icon" variant="ghost" onClick={() => openAg(a)}><Pencil className="h-3.5 w-3.5" /></Button>
                  <Button size="icon" variant="ghost" className="text-destructive" onClick={() => deleteAg(a)}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Übergeordnete Maßnahmen */}
        <section>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-2">
            <div>
              <h3 className="text-sm font-medium">Übergeordnete Maßnahmen</h3>
              <p className="text-xs text-muted-foreground">Eine operative Maßnahme kann (muss aber nicht) einer oder mehreren Vergaben zugeordnet werden.</p>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:shrink-0">
              <Select value={locFilter} onValueChange={setLocFilter}>
                <SelectTrigger className="w-full sm:w-[200px] rounded-full" data-testid="master-measure-loc-filter">
                  <SelectValue placeholder="Alle Standorte" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Standorte</SelectItem>
                  {locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button size="sm" variant="outline" onClick={exportAll} disabled={exportingAll} className="rounded-full w-full sm:w-auto" data-testid="export-master-measures-btn">
                {exportingAll ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Download className="h-4 w-4 mr-1" />} Export (XLSX)
              </Button>
              <Button size="sm" variant="outline" onClick={() => { setImportResult(null); setImportOpen(true); }} className="rounded-full w-full sm:w-auto" data-testid="import-master-measures-btn">
                <Upload className="h-4 w-4 mr-1" /> Import (XLSX)
              </Button>
              <Button size="sm" onClick={() => openMm()} className="rounded-full w-full sm:w-auto" data-testid="add-master-measure-btn">
                <Plus className="h-4 w-4 mr-1" /> Neue Vergabe
              </Button>
            </div>
          </div>
          {measures.length === 0 ? (
            <p className="text-sm text-muted-foreground py-3 text-center bg-secondary/20 rounded-md">Noch keine übergeordneten Maßnahmen angelegt.</p>
          ) : (
            <div className="space-y-2">
              {measures.map((m) => (
                <div key={m.id} className="rounded-md border border-border p-3 flex flex-col gap-2 sm:flex-row sm:gap-3" data-testid={`master-measure-row-${m.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium">{m.name}</span>
                      {!m.is_active && <Badge variant="secondary" className="text-xs">inaktiv</Badge>}
                      {m.vergabenummer && <Badge variant="outline" className="text-xs">Vergabe: {m.vergabenummer}</Badge>}
                      {m.los_nr && <Badge variant="outline" className="text-xs">Los: {m.los_nr}</Badge>}
                      {m.lfd_nr && <Badge variant="outline" className="text-xs">Lfd.: {m.lfd_nr}</Badge>}
                      {m.massnahmenummer && <Badge variant="outline" className="text-xs">MNR: {m.massnahmenummer}</Badge>}
                      {m.bamf_kursnummer && <Badge variant="outline" className="text-xs">BAMF: {m.bamf_kursnummer}</Badge>}
                      {(m.location_names || []).map((ln) => (
                        <Badge key={ln} variant="secondary" className="text-xs">{ln}</Badge>
                      ))}
                      {(m.location_names || []).length === 0 && <Badge variant="outline" className="text-xs text-muted-foreground">firmenweit</Badge>}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1 flex flex-wrap gap-x-2 gap-y-0.5">
                      {m.auftraggeber_name && <span>{m.auftraggeber_name}</span>}
                      {m.foerder_start && <span>· {isoToDe(m.foerder_start)} – {m.foerder_end ? isoToDe(m.foerder_end) : '?'}</span>}
                      {m.teilnehmer_plaetze ? <span>· {m.teilnehmer_plaetze} Plätze</span> : null}
                      <span>· {m.assigned_count} operative Maßnahme{m.assigned_count === 1 ? '' : 'n'}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-wrap justify-end border-t pt-2 sm:border-t-0 sm:pt-0 sm:shrink-0">
                    <Button size="icon" variant="ghost" title="Monats-Export (Excel)" onClick={() => setExpMeasure(m)} data-testid={`export-master-measure-${m.id}`}><Download className="h-3.5 w-3.5" /></Button>
                    <Button size="icon" variant="ghost" title="Maßnahmen zuordnen" onClick={() => setAssignMeasure(m)} data-testid={`assign-master-measure-${m.id}`}><Link2 className="h-3.5 w-3.5" /></Button>
                    <Button size="icon" variant="ghost" title="Kopieren" onClick={() => copyMm(m)} data-testid={`copy-master-measure-${m.id}`}><Copy className="h-3.5 w-3.5" /></Button>
                    <Button size="icon" variant="ghost" title="Bearbeiten" onClick={() => openMm(m)} data-testid={`edit-master-measure-${m.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                    <Button size="icon" variant="ghost" title="Löschen" className="text-destructive" onClick={() => deleteMm(m)} data-testid={`delete-master-measure-${m.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* XLSX-Import-Dialog */}
        <Dialog open={importOpen} onOpenChange={(o) => { if (!importing && !previewing) { setImportOpen(o); if (!o) resetImport(); } }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><FileSpreadsheet className="h-5 w-5 text-primary" /> Übergeordnete Maßnahmen importieren</DialogTitle>
              <DialogDescription>Excel hochladen → Vorschau prüfen → bestätigen. Abgleich über Vergabenummer + Los-Nr. + Lfd-Nr. + Name + Förderbeginn.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              {!importPreview && !importResult && (
                <>
                  <div className="rounded-md border border-border p-3 bg-secondary/20 text-xs text-muted-foreground space-y-1">
                    <p>• Pflichtfeld: <strong>Name</strong>. Datumsformat: TT.MM.JJJJ.</p>
                    <p>• Bestehende Vergaben werden über <strong>Vergabenummer + Los-Nr. + Lfd-Nr. + Name + Förderbeginn</strong> erkannt und <strong>aktualisiert</strong> (nur ausgefüllte Zellen überschreiben; leere bleiben unverändert).</p>
                    <p>• Gleichnamige Maßnahmen mit unterschiedlichem Förderbeginn werden als eigenständige Einträge angelegt. Exakte Doppelzeilen in der Datei werden übersprungen. Zeilen ohne Vergabenummer werden immer neu angelegt. Noch nicht vorhandene Auftraggeber werden automatisch angelegt. Spaltenreihenfolge egal.</p>
                  </div>
                  <Button variant="outline" className="w-full rounded-full" onClick={downloadTemplate} disabled={downloadingTemplate} data-testid="download-template-btn">
                    {downloadingTemplate ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Download className="h-4 w-4 mr-2" />} Vorlage herunterladen
                  </Button>
                  <input ref={importFileRef} type="file" accept=".xlsx,.xlsm" className="hidden" onChange={handleImportFile} data-testid="import-file-input" />
                  <Button className="w-full rounded-full" onClick={() => importFileRef.current?.click()} disabled={previewing} data-testid="choose-import-file-btn">
                    {previewing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />} Excel-Datei auswählen
                  </Button>
                </>
              )}

              {/* Schritt 2: Vorschau */}
              {importPreview && (
                <div className="space-y-3" data-testid="import-preview">
                  <div className="flex flex-wrap gap-2 text-sm">
                    <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">{importPreview.summary.create} neu anlegen</Badge>
                    <Badge className="bg-sky-100 text-sky-800 hover:bg-sky-100">{importPreview.summary.update} aktualisieren</Badge>
                    {importPreview.summary.skip > 0 && <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">{importPreview.summary.skip} übersprungen</Badge>}
                    {importPreview.summary.error > 0 && <Badge variant="destructive">{importPreview.summary.error} Fehler</Badge>}
                    <span className="text-muted-foreground ml-auto">{importPreview.total_rows} Zeile(n) gesamt</span>
                  </div>
                  <div className="rounded-md border border-border overflow-auto max-h-[45vh]">
                    <table className="w-full text-xs border-collapse">
                      <thead className="sticky top-0 bg-secondary/80 backdrop-blur">
                        <tr>
                          <th className="px-2 py-1.5 text-left font-medium border-b border-border">Aktion</th>
                          <th className="px-2 py-1.5 text-left font-medium border-b border-border">Name</th>
                          <th className="px-2 py-1.5 text-left font-medium border-b border-border whitespace-nowrap">Vergabe / Los / Lfd</th>
                          <th className="px-2 py-1.5 text-left font-medium border-b border-border whitespace-nowrap">Förderzeitraum</th>
                          <th className="px-2 py-1.5 text-left font-medium border-b border-border">Hinweise</th>
                        </tr>
                      </thead>
                      <tbody>
                        {importPreview.rows.map((r) => (
                          <tr key={r.row} className="odd:bg-secondary/20" data-testid={`import-preview-row-${r.row}`}>
                            <td className="px-2 py-1 border-b border-border/50">
                              {r.action === 'create' && <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 text-[10px]">Neu</Badge>}
                              {r.action === 'update' && <Badge className="bg-sky-100 text-sky-800 hover:bg-sky-100 text-[10px]">Update</Badge>}
                              {r.action === 'skip' && <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 text-[10px]">Übersprungen</Badge>}
                              {r.action === 'error' && <Badge variant="destructive" className="text-[10px]">Fehler</Badge>}
                            </td>
                            <td className="px-2 py-1 border-b border-border/50">
                              {r.name || '—'}
                              {r.action === 'update' && r.match_name && r.match_name !== r.name && (
                                <div className="text-[10px] text-muted-foreground">ersetzt: {r.match_name}</div>
                              )}
                            </td>
                            <td className="px-2 py-1 border-b border-border/50 font-mono whitespace-nowrap">{[r.vergabenummer, r.los_nr, r.lfd_nr].filter(Boolean).join(' / ') || '—'}</td>
                            <td className="px-2 py-1 border-b border-border/50 whitespace-nowrap">{r.foerder_start ? `${isoToDe(r.foerder_start)} – ${r.foerder_end ? isoToDe(r.foerder_end) : '?'}` : '—'}</td>
                            <td className="px-2 py-1 border-b border-border/50 text-amber-700">{(r.messages || []).join('; ')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="flex justify-end gap-2 pt-2 border-t">
                    <Button variant="outline" onClick={resetImport} disabled={importing}>Andere Datei</Button>
                    <Button onClick={confirmImport} disabled={importing || (importPreview.summary.create + importPreview.summary.update === 0)} data-testid="confirm-import-btn">
                      {importing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                      Import bestätigen ({importPreview.summary.create + importPreview.summary.update})
                    </Button>
                  </div>
                </div>
              )}

              {/* Ergebnis */}
              {importResult && (
                <div className="rounded-md border border-border p-3 space-y-2" data-testid="import-result">
                  <div className="flex items-center gap-2 text-sm font-medium text-emerald-700">
                    <CheckCircle2 className="h-4 w-4" /> {importResult.created} angelegt · {importResult.updated} aktualisiert{importResult.skipped ? ` · ${importResult.skipped} übersprungen` : ''}{importResult.auftraggeber_created ? ` · ${importResult.auftraggeber_created} Auftraggeber neu` : ''} (von {importResult.total_rows} Zeile{importResult.total_rows === 1 ? '' : 'n'})
                  </div>
                  {importResult.errors?.length > 0 && (
                    <div className="text-xs text-destructive space-y-0.5">
                      <div className="flex items-center gap-1 font-medium"><AlertTriangle className="h-3.5 w-3.5" /> Fehler ({importResult.errors.length}):</div>
                      {importResult.errors.slice(0, 8).map((er, i) => (<div key={i}>Zeile {er.row}: {er.message}</div>))}
                    </div>
                  )}
                  {importResult.warnings?.length > 0 && (
                    <div className="text-xs text-amber-700 space-y-0.5">
                      <div className="font-medium">Hinweise ({importResult.warnings.length}):</div>
                      {importResult.warnings.slice(0, 8).map((w, i) => (<div key={i}>Zeile {w.row}: {w.message}</div>))}
                    </div>
                  )}
                  <div className="flex justify-end gap-2 pt-1">
                    <Button variant="outline" size="sm" onClick={resetImport} data-testid="import-again-btn">Weitere Datei importieren</Button>
                  </div>
                </div>
              )}

              <div className="flex justify-end pt-2 border-t">
                <Button variant="outline" onClick={() => { setImportOpen(false); resetImport(); }} disabled={importing || previewing}>Schließen</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Export-Dialog */}
        <Dialog open={!!expMeasure} onOpenChange={(o) => { if (!o) setExpMeasure(null); }}>          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Monats-Export — {expMeasure?.name}</DialogTitle>
              <DialogDescription className="sr-only">Monat wählen und Vorschau der zu exportierenden Zeiten prüfen.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Exportiert die eingeteilten Zeiten aller zugeordneten operativen Maßnahmen als Excel-Datei
                (eine Zeile pro Werktag, pro Mitarbeiter: MA / Profession / Umfang).
              </p>
              <div className="flex items-center flex-wrap gap-1.5 text-xs" data-testid="export-standorte">
                <span className="text-muted-foreground">Standort(e):</span>
                {(expMeasure?.location_names || []).length > 0
                  ? (expMeasure.location_names || []).map((ln) => (<Badge key={ln} variant="secondary" className="text-xs">{ln}</Badge>))
                  : <Badge variant="outline" className="text-xs text-muted-foreground">firmenweit</Badge>}
              </div>
              <div className="max-w-[200px]">
                <Label>Monat</Label>
                <Input type="month" value={expMonth} onChange={(e) => setExpMonth(e.target.value)} data-testid="export-month-input" />
              </div>

              {/* Vorschau */}
              {previewLoading ? (
                <div className="flex items-center justify-center py-8 text-muted-foreground">
                  <Loader2 className="h-5 w-5 animate-spin mr-2" /> Vorschau wird geladen…
                </div>
              ) : preview && preview.employees.length === 0 && (!preview.honorar || preview.honorar.employees.length === 0) ? (
                <p className="text-sm text-muted-foreground py-4 text-center bg-secondary/20 rounded-md" data-testid="export-preview-empty">
                  Keine Einsätze im {preview.monat_label} gefunden — die Export-Datei wäre leer.
                </p>
              ) : preview ? (
                <div className="space-y-2" data-testid="export-preview">
                  {preview.massnahmen_ohne_zeit?.length > 0 && (
                    <div className="text-xs rounded-md border border-sky-300 bg-sky-50 text-sky-800 px-3 py-2" data-testid="export-preview-no-time-warning">
                      ℹ Folgende operative Maßnahme(n) haben <strong>keine Durchführungszeit</strong> hinterlegt:{' '}
                      {preview.massnahmen_ohne_zeit.join(', ')} — „Ganztags"-Schichten dieser Maßnahmen werden mit <strong>8 Std. (08:00–16:00)</strong> als Standard-Vollzeit gezählt.
                      Du kannst die genaue Durchführungszeit unter „Maßnahmen verwalten" → Bearbeiten → Durchführungszeit pflegen.
                    </div>
                  )}
                  {preview.employees.some((e) => e.position_missing) && (
                    <div className="text-xs rounded-md border border-amber-300 bg-amber-50 text-amber-800 px-3 py-2" data-testid="export-preview-warning">
                      ⚠ Bei {preview.employees.filter((e) => e.position_missing).length} Mitarbeiter(n) fehlt die Angabe „Einsatz als" —
                      die Spalte Profession bleibt im Export leer. Pflegbar im Mitarbeiterprofil unter „Beschäftigung".
                    </div>
                  )}
                  {preview.employees.length > 0 && (
                  <div className="rounded-md border border-border overflow-auto max-h-[40vh]">
                    <table className="w-full text-xs border-collapse">
                      <thead className="sticky top-0 bg-secondary/80 backdrop-blur">
                        <tr>
                          <th className="px-2 py-1.5 text-left font-medium border-b border-border whitespace-nowrap">Datum</th>
                          {preview.employees.map((e) => (
                            <th key={e.id} className="px-2 py-1.5 text-center font-medium border-b border-l border-border whitespace-nowrap">
                              {e.last_name}
                              <div className={`font-normal ${e.position_missing ? 'text-amber-600' : 'text-muted-foreground'}`}>
                                {e.position_missing ? '„Einsatz als" fehlt' : e.position}
                              </div>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {preview.days.map((d) => (
                          <tr key={d.date} className="odd:bg-secondary/20">
                            <td className="px-2 py-1 whitespace-nowrap border-b border-border/50">{d.label}</td>
                            {d.cells.map((c, i) => (
                              <td key={i} className="px-2 py-1 text-center border-b border-l border-border/50">
                                {c === null ? <span className="text-muted-foreground italic">abwesend</span> : c.umfang}
                              </td>
                            ))}
                          </tr>
                        ))}
                        <tr className="font-semibold bg-secondary/40">
                          <td className="px-2 py-1.5">Summe</td>
                          {preview.totals.map((t, i) => (
                            <td key={i} className="px-2 py-1.5 text-center border-l border-border/50" data-testid={`export-preview-total-${i}`}>{t}</td>
                          ))}
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  )}
                  {preview.employees.length > 0 && (
                    <p className="text-[11px] text-muted-foreground">
                      Abwesende Tage (genehmigter Urlaub/Krankheit) werden im Export komplett leer gelassen.
                    </p>
                  )}
                  {preview.honorar && preview.honorar.employees.length > 0 && (
                    <div className="space-y-1" data-testid="export-preview-honorar">
                      <div className="text-xs font-semibold text-muted-foreground pt-1">Honorarkräfte</div>
                      <div className="rounded-md border border-border overflow-auto max-h-[40vh]">
                        <table className="w-full text-xs border-collapse">
                          <thead className="sticky top-0 bg-secondary/80 backdrop-blur">
                            <tr>
                              <th className="px-2 py-1.5 text-left font-medium border-b border-border whitespace-nowrap">Datum</th>
                              {preview.honorar.employees.map((e) => (
                                <th key={e.id} className="px-2 py-1.5 text-center font-medium border-b border-l border-border whitespace-nowrap">
                                  {e.last_name}
                                  <div className="font-normal text-muted-foreground">{e.position || '—'}</div>
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {preview.honorar.days.map((d) => (
                              <tr key={d.date} className="odd:bg-secondary/20">
                                <td className="px-2 py-1 whitespace-nowrap border-b border-border/50">{d.label}</td>
                                {d.cells.map((c, i) => (
                                  <td key={i} className="px-2 py-1 text-center border-b border-l border-border/50">{c.umfang}</td>
                                ))}
                              </tr>
                            ))}
                            <tr className="font-semibold bg-secondary/40">
                              <td className="px-2 py-1.5">Summe</td>
                              {preview.honorar.totals.map((t, i) => (
                                <td key={i} className="px-2 py-1.5 text-center border-l border-border/50" data-testid={`export-preview-hk-total-${i}`}>{t}</td>
                              ))}
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              ) : null}

              <div className="flex justify-end gap-2 pt-2 border-t">
                <Button variant="outline" onClick={() => setExpMeasure(null)}>Abbrechen</Button>
                <Button onClick={runExport} disabled={exporting || !expMonth || (preview && preview.employees.length === 0 && (!preview.honorar || preview.honorar.employees.length === 0))} data-testid="export-run-btn">
                  {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Download className="h-4 w-4 mr-1" /> Exportieren</>}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Auftraggeber-Dialog */}
        <Dialog open={agOpen} onOpenChange={setAgOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{agEdit ? 'Auftraggeber bearbeiten' : 'Neuer Auftraggeber'}</DialogTitle>
              <DialogDescription className="sr-only">Auftraggeber-Stammdaten erfassen.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div><Label>Name *</Label><Input value={agForm.name} onChange={(e) => setAgForm({ ...agForm, name: e.target.value })} /></div>
              <div>
                <Label>Art</Label>
                <Select value={agForm.art} onValueChange={(v) => setAgForm({ ...agForm, art: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(ART_LABEL).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label>Ansprechpartner</Label><Input value={agForm.ansprechpartner || ''} onChange={(e) => setAgForm({ ...agForm, ansprechpartner: e.target.value })} /></div>
                <div><Label>Telefon</Label><Input value={agForm.telefon || ''} onChange={(e) => setAgForm({ ...agForm, telefon: e.target.value })} /></div>
                <div className="col-span-2"><Label>E-Mail</Label><Input value={agForm.email || ''} onChange={(e) => setAgForm({ ...agForm, email: e.target.value })} /></div>
                <div className="col-span-2"><Label>Straße</Label><Input value={agForm.strasse || ''} onChange={(e) => setAgForm({ ...agForm, strasse: e.target.value })} /></div>
                <div><Label>PLZ</Label><Input value={agForm.plz || ''} onChange={(e) => setAgForm({ ...agForm, plz: e.target.value })} /></div>
                <div><Label>Ort</Label><Input value={agForm.ort || ''} onChange={(e) => setAgForm({ ...agForm, ort: e.target.value })} /></div>
              </div>
              <div><Label>Notizen</Label><Textarea value={agForm.notes || ''} onChange={(e) => setAgForm({ ...agForm, notes: e.target.value })} rows={2} /></div>
              <div className="flex justify-end gap-2 pt-2 border-t">
                <Button variant="outline" onClick={() => setAgOpen(false)}>Abbrechen</Button>
                <Button onClick={saveAg} disabled={saving} data-testid="auftraggeber-save">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Speichern'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Master-Maßnahme Dialog */}
        <Dialog open={mmOpen} onOpenChange={setMmOpen}>
          <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{mmEdit ? 'Übergeordnete Maßnahme bearbeiten' : (mmIsCopy ? 'Übergeordnete Maßnahme kopieren' : 'Neue übergeordnete Maßnahme')}</DialogTitle>
              <DialogDescription className="sr-only">Stammdaten der übergeordneten Maßnahme (Vergabe) erfassen.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div><Label>Name * (z.B. „AVGS Coaching 2026&quot;)</Label>
                <Input value={mmForm.name} onChange={(e) => setMmForm({ ...mmForm, name: e.target.value })} data-testid="master-measure-name" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label>Vergabenummer</Label><Input value={mmForm.vergabenummer || ''} onChange={(e) => setMmForm({ ...mmForm, vergabenummer: e.target.value })} className="font-mono" /></div>
                <div><Label>Los-Nr.</Label><Input value={mmForm.los_nr || ''} onChange={(e) => setMmForm({ ...mmForm, los_nr: e.target.value })} className="font-mono" data-testid="master-measure-los-nr" /></div>
                <div><Label>Lfd. Nr.</Label><Input value={mmForm.lfd_nr || ''} onChange={(e) => setMmForm({ ...mmForm, lfd_nr: e.target.value })} className="font-mono" data-testid="master-measure-lfd-nr" /></div>
                <div><Label>Maßnahmennummer</Label><Input value={mmForm.massnahmenummer || ''} onChange={(e) => setMmForm({ ...mmForm, massnahmenummer: e.target.value })} className="font-mono" /></div>
              </div>
              <div>
                <Label>BAMF-Maßnahmenummer</Label>
                <Input
                  value={mmForm.bamf_kursnummer || ''}
                  onChange={(e) => setMmForm({ ...mmForm, bamf_kursnummer: e.target.value })}
                  className="font-mono"
                  placeholder="z.B. 805353-TH-79-2024"
                  data-testid="master-measure-bamf-nummer"
                />
                <p className="text-xs text-muted-foreground mt-1">Spezieller BAMF-Nummernblock für Kurse (optional).</p>
              </div>
              <div>
                <Label>Auftraggeber</Label>
                <Select value={mmForm.auftraggeber_id || 'none'} onValueChange={(v) => setMmForm({ ...mmForm, auftraggeber_id: v === 'none' ? '' : v })}>
                  <SelectTrigger><SelectValue placeholder="Wählen…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Kein Auftraggeber —</SelectItem>
                    {auftraggeber.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Standorte (leer = firmenweit)</Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-40 overflow-y-auto rounded-md border p-3 mt-1">
                  {locations.map((l) => (
                    <label key={l.id} className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox
                        checked={(mmForm.location_ids || []).includes(l.id)}
                        onCheckedChange={() => toggleMmLoc(l.id)}
                        data-testid={`mm-location-${l.id}`}
                      />
                      {l.name}
                    </label>
                  ))}
                  {locations.length === 0 && <p className="text-xs text-muted-foreground">Keine Standorte verfügbar.</p>}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label>Förderzeitraum von</Label>
                  <DatePickerDE value={mmForm.foerder_start} onChange={(v) => setMmForm({ ...mmForm, foerder_start: v })} placeholder="Beginn" />
                </div>
                <div><Label>Förderzeitraum bis</Label>
                  <DatePickerDE value={mmForm.foerder_end} onChange={(v) => setMmForm({ ...mmForm, foerder_end: v })} placeholder="Ende" />
                </div>
              </div>
              {mmIsCopy && (mmForm.foerder_start || mmForm.foerder_end) && (
                <Button
                  type="button" size="sm" variant="outline"
                  className="rounded-full h-7 text-xs"
                  onClick={shiftFoerderYear}
                  data-testid="shift-foerder-year-btn"
                >
                  <CalendarPlus className="h-3.5 w-3.5 mr-1" /> Förderzeitraum +1 Jahr
                </Button>
              )}
              <div className="flex items-center gap-2 p-2 rounded bg-secondary/30">
                <Checkbox checked={mmForm.verlaengerungs_option} onCheckedChange={(c) => setMmForm({ ...mmForm, verlaengerungs_option: c })} />
                <Label className="font-normal flex-1">Verlängerungs-Option vorhanden</Label>
                {mmForm.verlaengerungs_option && (
                  <DatePickerDE value={mmForm.verlaengerung_max_end} onChange={(v) => setMmForm({ ...mmForm, verlaengerung_max_end: v })} placeholder="Max. Ende" />
                )}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label>Teilnehmerplätze</Label>
                  <Input type="number" min="0" value={mmForm.teilnehmer_plaetze} onChange={(e) => setMmForm({ ...mmForm, teilnehmer_plaetze: e.target.value })} />
                </div>
                <div><Label>Budget (€)</Label>
                  <Input type="number" min="0" step="0.01" value={mmForm.budget} onChange={(e) => setMmForm({ ...mmForm, budget: e.target.value })} />
                </div>
              </div>
              <div><Label>Notizen</Label><Textarea rows={2} value={mmForm.notes || ''} onChange={(e) => setMmForm({ ...mmForm, notes: e.target.value })} /></div>
              <div className="flex items-center gap-2">
                <Checkbox checked={mmForm.is_active} onCheckedChange={(c) => setMmForm({ ...mmForm, is_active: c })} />
                <Label className="font-normal">Aktiv</Label>
              </div>

              {/* Beim Kopieren: Auswahl, welche operativen Maßnahmen-Zuordnungen übernommen werden */}
              {mmIsCopy && (
                <div className="rounded-md border border-border p-3 bg-secondary/20" data-testid="copy-assign-section">
                  <Label className="text-sm font-medium">Zugeordnete operative Maßnahmen übernehmen</Label>
                  <p className="text-xs text-muted-foreground mt-0.5 mb-2">
                    Wähle, welche Zuordnungen der Quell-Vergabe auch mit der Kopie verknüpft werden. Die Original-Vergabe bleibt unverändert.
                  </p>
                  {loadingAssigned ? (
                    <div className="flex items-center text-sm text-muted-foreground py-2"><Loader2 className="h-4 w-4 animate-spin mr-2" /> Lädt…</div>
                  ) : assignedMassnahmen.length === 0 ? (
                    <p className="text-xs text-muted-foreground py-1">Keine operativen Maßnahmen zugeordnet.</p>
                  ) : (
                    <>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-muted-foreground" data-testid="copy-assign-count">
                          {selectedAssignIds.length} von {assignedMassnahmen.length} ausgewählt
                        </span>
                        <Button
                          type="button" size="sm" variant="ghost" className="h-6 text-xs"
                          onClick={() => setSelectedAssignIds(
                            selectedAssignIds.length === assignedMassnahmen.length ? [] : assignedMassnahmen.map((x) => x.id)
                          )}
                          data-testid="copy-assign-toggle-all"
                        >
                          {selectedAssignIds.length === assignedMassnahmen.length ? 'Keine' : 'Alle'}
                        </Button>
                      </div>
                      <div className="space-y-1 max-h-[180px] overflow-y-auto">
                        {assignedMassnahmen.map((a) => (
                          <label
                            key={a.id}
                            className="flex items-center gap-2 rounded px-2 py-1.5 hover:bg-secondary/40 cursor-pointer"
                            data-testid={`copy-assign-item-${a.id}`}
                          >
                            <Checkbox
                              checked={selectedAssignIds.includes(a.id)}
                              onCheckedChange={() => toggleAssign(a.id)}
                              data-testid={`copy-assign-checkbox-${a.id}`}
                            />
                            <span className="text-sm flex-1 min-w-0 truncate">
                              {a.name}
                              {a.short_code ? <span className="text-muted-foreground"> ({a.short_code})</span> : null}
                            </span>
                            {a.location_name && <span className="text-xs text-muted-foreground flex-shrink-0">{a.location_name}</span>}
                            {!a.is_active && <Badge variant="secondary" className="text-xs flex-shrink-0">inaktiv</Badge>}
                          </label>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2 border-t">
                {mmEdit && (
                  <Button
                    variant="outline"
                    className="mr-auto"
                    onClick={() => { setMmOpen(false); setAssignMeasure(mmEdit); }}
                    data-testid="assign-from-dialog-btn"
                  >
                    <Link2 className="h-4 w-4 mr-2" /> Maßnahmen zuordnen
                  </Button>
                )}
                <Button variant="outline" onClick={() => setMmOpen(false)}>Abbrechen</Button>
                <Button onClick={saveMm} disabled={saving} data-testid="master-measure-save">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Speichern'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        <AssignMassnahmenDialog
          measure={assignMeasure}
          onClose={() => setAssignMeasure(null)}
          onSaved={load}
        />
      </CardContent>
    </Card>
  );
};
